import { useState, useRef, useEffect } from 'react';
import {
  ChevronLeft,
  Users,
  CheckCircle,
  XCircle,
  Clock,
  Calendar,
  ChevronDown,
  ChevronRight,
  Activity,
  Download,
  Edit3,
  Eye,
  X,
  Bot,
  Send
} from 'lucide-react';

function ProcessFlowchartVisualization({ selectedProcesses, processDetails, onBack, showChat = false }) {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedControl, setSelectedControl] = useState(null);
  const [expandedProcess, setExpandedProcess] = useState(null);
  const [chatMessages, setChatMessages] = useState({});
  const [currentMessage, setCurrentMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const chatEndRef = useRef(null);
  const messageInputRef = useRef(null);

  // Get the current active process
  const activeProcess = processDetails[selectedProcesses[activeTab]];
  const activeProcessId = selectedProcesses[activeTab];

  // Initialize chat messages for each process
  useEffect(() => {
    const initialMessages = {};
    selectedProcesses.forEach(processId => {
      if (!chatMessages[processId]) {
        const process = processDetails[processId];
        initialMessages[processId] = [{
          type: 'ai',
          content: `I've generated the ${process?.name || 'Process'} flowchart with ${process?.steps?.length || 0} steps. You can now refine this process by asking me to modify steps, add controls, change owners, or adjust the workflow. What would you like to update?`,
          timestamp: new Date().toISOString()
        }];
      }
    });
    if (Object.keys(initialMessages).length > 0) {
      setChatMessages(prev => ({ ...prev, ...initialMessages }));
    }
  }, [selectedProcesses, processDetails]);

  // Scroll to bottom of chat when new messages arrive
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, activeProcessId]);

  // Generate control IDs for each step
  const generateControlId = (processIndex, stepIndex) => {
    const prefixes = ['OTC', 'RTC', 'PTC', 'HTC', 'REV', 'CAN'];
    const prefix = prefixes[processIndex % prefixes.length];
    return `${prefix}-${String(stepIndex + 1).padStart(2, '0')}`;
  };

  // Determine control status based on risks
  const getControlStatus = (step, risks) => {
    if (!risks || risks.length === 0) return 'complete';
    
    // Check if step has high-risk indicators
    const hasHighRisk = risks.some(risk => 
      risk.toLowerCase().includes('critical') || 
      risk.toLowerCase().includes('high') ||
      risk.toLowerCase().includes('significant')
    );
    
    if (hasHighRisk) return 'pending';
    if (step.toLowerCase().includes('validate') || step.toLowerCase().includes('reconcile')) return 'active';
    return 'complete';
  };

  // Get status color and icon
  const getStatusConfig = (status) => {
    switch (status) {
      case 'complete':
        return {
          bgColor: 'bg-green-50 border-green-500',
          textColor: 'text-green-700',
          badgeBg: 'bg-green-900 text-white',
          icon: CheckCircle,
          label: 'Complete'
        };
      case 'active':
        return {
          bgColor: 'bg-blue-50 border-blue-500',
          textColor: 'text-blue-700',
          badgeBg: 'bg-blue-600 text-white',
          icon: Activity,
          label: 'Active'
        };
      case 'pending':
        return {
          bgColor: 'bg-gray-50 border-gray-400',
          textColor: 'text-gray-700',
          badgeBg: 'bg-gray-600 text-white',
          icon: Clock,
          label: 'Pending'
        };
      default:
        return {
          bgColor: 'bg-gray-50 border-gray-300',
          textColor: 'text-gray-600',
          badgeBg: 'bg-gray-500 text-white',
          icon: Clock,
          label: 'Pending'
        };
    }
  };

  // Get risk rating configuration
  const getRiskRating = (risks) => {
    if (!risks || risks.length === 0) return { label: 'Low Risk', color: 'bg-green-100 text-green-700' };
    
    const hasHighRisk = risks.some(risk => 
      risk.toLowerCase().includes('critical') || 
      risk.toLowerCase().includes('high')
    );
    
    if (hasHighRisk) return { label: 'High Risk', color: 'bg-red-100 text-red-700' };
    return { label: 'Medium Risk', color: 'bg-yellow-100 text-yellow-700' };
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={onBack}
              className="text-gray-600 hover:text-gray-900"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            {showChat ? (
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Bot className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold">Design from Template</h2>
                  <p className="text-sm text-gray-500">Customize your process template with interactive visual feedback</p>
                </div>
              </div>
            ) : (
              <div>
                <h2 className="text-xl font-semibold">SOX Process Flowchart</h2>
                <p className="text-sm text-gray-500">Visualizing {selectedProcesses.length} selected process(es)</p>
              </div>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <button className="px-4 py-2 text-gray-600 hover:text-gray-900 flex items-center space-x-2">
              <Download className="h-4 w-4" />
              <span>Export Processes</span>
            </button>
            <button className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 flex items-center space-x-2">
              <CheckCircle className="h-4 w-4" />
              <span>Complete Setup</span>
            </button>
          </div>
        </div>
      </div>

      {/* Process Tabs */}
      {showChat && selectedProcesses.length > 1 && (
        <div className="bg-white border-b px-6 py-2">
          <div className="flex space-x-1">
            {selectedProcesses.map((processId, index) => {
              const process = processDetails[processId];
              return (
                <button
                  key={processId}
                  onClick={() => setActiveTab(index)}
                  className={`px-4 py-2 rounded-t-lg font-medium text-sm transition-all ${
                    index === activeTab
                      ? 'bg-gray-100 text-gray-900 border-b-2 border-purple-600'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {process?.name || `Process ${index + 1}`}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex h-[calc(100vh-73px)]">
        {/* Left Sidebar - Chat or Process List */}
        <div className={`${showChat ? 'w-96' : 'w-64'} bg-white border-r overflow-hidden flex flex-col`}>
          {showChat ? (
            // Chat Interface
            <>
            
              
              {/* Chat Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {(chatMessages[activeProcessId] || []).map((message, index) => (
                  <div
                    key={index}
                    className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[80%] ${message.type === 'user' ? 'order-2' : 'order-1'}`}>
                      {message.type === 'ai' && (
                        <div className="flex items-start space-x-2 mb-2">
                          <div className="p-1.5 bg-purple-100 rounded-lg">
                            <Bot className="h-4 w-4 text-purple-600" />
                          </div>
                        </div>
                      )}
                      <div
                        className={`px-4 py-3 rounded-2xl ${
                          message.type === 'user'
                            ? 'bg-gray-900 text-white'
                            : 'bg-gray-100 text-gray-900'
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                      </div>
                      <p className="text-xs text-gray-400 mt-1 px-2">
                        {new Date(message.timestamp).toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                ))}
                {isTyping && (
                  <div className="flex justify-start">
                    <div className="max-w-[80%]">
                      <div className="flex items-start space-x-2 mb-2">
                        <div className="p-1.5 bg-purple-100 rounded-lg">
                          <Bot className="h-4 w-4 text-purple-600" />
                        </div>
                      </div>
                      <div className="bg-gray-100 px-4 py-3 rounded-2xl">
                        <div className="flex space-x-1">
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>
              
              {/* Chat Input */}
              <div className="p-4 border-t bg-white">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (currentMessage.trim()) {
                      // Add user message
                      const newMessage = {
                        type: 'user',
                        content: currentMessage,
                        timestamp: new Date().toISOString()
                      };
                      setChatMessages(prev => ({
                        ...prev,
                        [activeProcessId]: [...(prev[activeProcessId] || []), newMessage]
                      }));
                      setCurrentMessage('');
                      
                      // Simulate AI response
                      setIsTyping(true);
                      setTimeout(() => {
                        const aiResponse = {
                          type: 'ai',
                          content: `I understand you want to modify the ${activeProcess?.name}. I can help you with:\n\n• Adding or removing process steps\n• Updating control points\n• Changing responsible parties\n• Adjusting workflow connections\n\nWhat specific changes would you like to make?`,
                          timestamp: new Date().toISOString()
                        };
                        setChatMessages(prev => ({
                          ...prev,
                          [activeProcessId]: [...(prev[activeProcessId] || []), aiResponse]
                        }));
                        setIsTyping(false);
                      }, 1500);
                    }
                  }}
                  className="flex items-center space-x-2"
                >
                  <input
                    ref={messageInputRef}
                    type="text"
                    value={currentMessage}
                    onChange={(e) => setCurrentMessage(e.target.value)}
                    placeholder={`Ask me to modify the ${activeProcess?.name}...`}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <button
                    type="submit"
                    disabled={!currentMessage.trim() || isTyping}
                    className={`p-2 rounded-lg transition-all ${
                      currentMessage.trim() && !isTyping
                        ? 'bg-gray-900 text-white hover:bg-gray-800'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    <Send className="h-5 w-5" />
                  </button>
                </form>
              </div>
            </>
          ) : (
            // Original Cycle Information
            <div className="p-4">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Cycle Information
              </h3>
            
            <div className="space-y-3 mb-6">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Status</span>
                <span className="font-medium">Active</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Processes</span>
                <span className="font-medium">{selectedProcesses.length} processes</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Last Run</span>
                <span className="font-medium">Jan 15, 2025</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Next Due</span>
                <span className="font-medium">Feb 15, 2025</span>
              </div>
            </div>

            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Processes
            </h3>

            {/* Process Tabs */}
            <div className="space-y-2">
              {selectedProcesses.map((processId, index) => {
                const process = processDetails[processId];
                const isActive = index === activeTab;
                const status = getControlStatus(process.name, process.risks);
                const statusConfig = getStatusConfig(status);
                
                return (
                  <div
                    key={processId}
                    onClick={() => setActiveTab(index)}
                    className={`cursor-pointer rounded-lg border transition-all ${
                      isActive ? 'bg-gray-50 border-gray-900' : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="p-3">
                      <div className="flex items-center justify-between mb-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedProcess(expandedProcess === processId ? null : processId);
                          }}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          {expandedProcess === processId ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </button>
                        <span className="text-xs text-gray-500">
                          {process.steps ? process.steps.length : 0} steps
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-900">
                          {process.name}
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-2 mt-2">
                        <Users className="h-3 w-3 text-gray-400" />
                        <span className="text-xs text-gray-600">
                          {process.responsible_parties?.[0] || 'Finance Ops'}
                        </span>
                      </div>
                      
                      <div className="mt-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${statusConfig.badgeBg}`}>
                          {statusConfig.label}
                        </span>
                      </div>
                    </div>
                    
                    {expandedProcess === processId && (
                      <div className="border-t px-3 py-2 text-xs text-gray-600">
                        <div>{process.steps?.length || 0} steps</div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          )}
        </div>

        {/* Main Content - Flowchart */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {activeProcess && (
            <>
              {/* Flowchart Visualization */}
              <div className="flex-1 bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
                  <h2 className="text-lg font-semibold text-gray-900">Comprehensive {activeProcess.name} assessment</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Assessment conducted to identify control gaps in {activeProcess.category || 'business'} processes, including revenue streams identification and visibility.
                  </p>
                </div>
                
                {/* Scrollable Flowchart Area with Grid Background */}
                <div className="flex-1 overflow-auto bg-gray-50" style={{ 
                  backgroundImage: `linear-gradient(to right, #e5e7eb 1px, transparent 1px), linear-gradient(to bottom, #e5e7eb 1px, transparent 1px)`,
                  backgroundSize: '20px 20px'
                }}>
                  <div className="p-6">
                    {/* Process Header Info */}
                    {showChat && (
                      <div className="mb-6">
                        <h2 className="text-xl font-semibold text-gray-900">
                          {activeProcess?.name || 'Process'}
                        </h2>
                        <p className="text-sm text-gray-600 mt-1">
                          {activeProcess?.steps?.length || 0} steps • {activeProcess?.controls?.length || 0} controls
                        </p>
                      </div>
                    )}
                    
                    {/* Process flow container */}
                    <div className="relative">
                      {/* Calculate layout */}
                      {(() => {
                        const itemsPerRow = 3; // 3 items per row to fit better
                        const totalSteps = activeProcess.steps?.length || 0;
                        const rows = Math.ceil(totalSteps / itemsPerRow);
                        
                        return (
                          <>
                            {/* Render rows */}
                            {Array.from({ length: rows }, (_, rowIndex) => {
                              const startIdx = rowIndex * itemsPerRow;
                              const endIdx = Math.min(startIdx + itemsPerRow, totalSteps);
                              const stepsInRow = activeProcess.steps.slice(startIdx, endIdx);
                              const isEvenRow = rowIndex % 2 === 0;
                              
                              return (
                                <div key={rowIndex} className={`mb-8 ${rowIndex === rows - 1 ? 'mb-0' : ''}`}>
                                  <div className={`flex items-start gap-8 ${isEvenRow ? '' : 'flex-row-reverse'}`}>
                                    {stepsInRow.map((step, indexInRow) => {
                                      const stepIndex = startIdx + indexInRow;
                                      const stepDesc = typeof step === 'object' ? step.description : step;
                                      const tool = typeof step === 'object' ? step.tool : null;
                                      const controlId = generateControlId(activeTab, stepIndex);
                                      const status = getControlStatus(stepDesc, activeProcess.risks);
                                      
                                      const isLastInRow = indexInRow === stepsInRow.length - 1;
                                      const isLastStep = stepIndex === totalSteps - 1;
                                      const hasNextRow = rowIndex < rows - 1 && isLastInRow && !isLastStep;
                                      
                                      // Determine node colors based on status
                                      const nodeStyle = status === 'complete' 
                                        ? 'bg-green-50 border-green-400 border-2' 
                                        : status === 'active' 
                                        ? 'bg-blue-50 border-blue-400 border-2'
                                        : 'bg-white border-gray-300 border-2';
                                      
                                      const statusBadge = status === 'complete'
                                        ? 'bg-gray-900 text-white'
                                        : status === 'active'
                                        ? 'bg-gray-900 text-white'
                                        : 'bg-gray-600 text-white';
                                        
                                      // Control badge colors cycling through red and yellow
                                      const controlBadgeColor = stepIndex % 2 === 0 
                                        ? 'bg-red-500' 
                                        : 'bg-yellow-500';
                                      
                                      return (
                                        <div 
                                          key={stepIndex} 
                                          className="relative flex-1 max-w-sm"
                                        >
                                          {/* Control ID Badge */}
                                          <div className="absolute -top-4 left-4 z-10">
                                            <div className={`${controlBadgeColor} text-white px-2.5 py-1 rounded-full text-xs font-bold shadow-md`}>
                                              {controlId}
                                            </div>
                                          </div>
                                          
                                          {/* Node */}
                                          <div
                                            className={`relative p-4 rounded-lg ${nodeStyle} cursor-pointer hover:shadow-lg transition-all`}
                                            onClick={() => setSelectedControl({
                                              id: controlId,
                                              step: stepDesc,
                                              tool,
                                              status,
                                              risks: activeProcess.risks,
                                              controls: activeProcess.controls,
                                              responsibleParty: activeProcess.responsible_parties?.[0]
                                            })}
                                            style={{ minHeight: '120px' }}
                                          >
                                            <h3 className="font-medium text-gray-900 mb-2 text-sm leading-relaxed">
                                              {stepDesc}
                                            </h3>
                                            
                                            <div className="flex items-center space-x-2 text-gray-600 mb-2">
                                              <Users className="h-3 w-3" />
                                              <span className="text-xs">
                                                {activeProcess.responsible_parties?.[stepIndex] || 
                                                 activeProcess.responsible_parties?.[0] || 
                                                 'Sameer Khan'}
                                              </span>
                                            </div>
                                            
                                            <div className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${statusBadge}`}>
                                              {status}
                                            </div>
                                          </div>
                                          
                                          {/* Horizontal Arrow Connector */}
                                          {!isLastInRow && indexInRow < stepsInRow.length - 1 && (
                                            <div className={`absolute top-1/2 ${isEvenRow ? 'left-full ml-2' : 'right-full mr-2'} transform -translate-y-1/2`}>
                                              <svg width="28" height="2" className="overflow-visible">
                                                <defs>
                                                  <marker
                                                    id={`arrow-${stepIndex}`}
                                                    markerWidth="8"
                                                    markerHeight="8"
                                                    refX="8"
                                                    refY="1"
                                                    orient="auto"
                                                  >
                                                    <polygon
                                                      points="0 0, 8 1, 0 2"
                                                      fill="#9CA3AF"
                                                    />
                                                  </marker>
                                                </defs>
                                                <line
                                                  x1="0"
                                                  y1="1"
                                                  x2="28"
                                                  y2="1"
                                                  stroke="#9CA3AF"
                                                  strokeWidth="2"
                                                  markerEnd={isEvenRow ? `url(#arrow-${stepIndex})` : ''}
                                                  markerStart={!isEvenRow ? `url(#arrow-${stepIndex})` : ''}
                                                />
                                              </svg>
                                            </div>
                                          )}
                                          
                                          {/* Vertical Arrow to Next Row */}
                                          {hasNextRow && (
                                            <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2">
                                              <svg width="2" height="32" className="overflow-visible">
                                                <defs>
                                                  <marker
                                                    id={`arrow-down-${stepIndex}`}
                                                    markerWidth="8"
                                                    markerHeight="8"
                                                    refX="1"
                                                    refY="8"
                                                    orient="auto"
                                                  >
                                                    <polygon
                                                      points="0 0, 2 0, 1 8"
                                                      fill="#9CA3AF"
                                                    />
                                                  </marker>
                                                </defs>
                                                <line
                                                  x1="1"
                                                  y1="0"
                                                  x2="1"
                                                  y2="32"
                                                  stroke="#9CA3AF"
                                                  strokeWidth="2"
                                                  markerEnd={`url(#arrow-down-${stepIndex})`}
                                                />
                                              </svg>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Right Sidebar - Control Details */}
        {selectedControl && (
          <div className="w-96 bg-white border-l overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold">Control {selectedControl.id}</h2>
                <button
                  onClick={() => setSelectedControl(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-5 w-5" />
                </button>
              </div>

              {/* Control Overview */}
              <div className="space-y-4 mb-6">
                <div>
                  <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                    Control Overview
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div>
                      <p className="text-xs text-gray-500">Control ID</p>
                      <p className="font-medium">{selectedControl.id}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Title</p>
                      <p className="font-medium">{selectedControl.step}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Frequency</p>
                      <p className="font-medium flex items-center">
                        <Calendar className="h-3 w-3 mr-1" />
                        Monthly
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Control Owner</p>
                      <p className="font-medium flex items-center">
                        <Users className="h-3 w-3 mr-1" />
                        {selectedControl.responsibleParty || 'Finance Ops Team'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Status & Risk */}
                <div>
                  <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                    Status & Risk
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div>
                      <p className="text-xs text-gray-500">Current Status</p>
                      <div className="flex items-center space-x-2 mt-1">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                          Effective
                        </span>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Risk Rating</p>
                      <div className="mt-1">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          getRiskRating(selectedControl.risks).color
                        }`}>
                          {getRiskRating(selectedControl.risks).label}
                        </span>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Nature</p>
                      <p className="text-sm">IT Dependent Manual</p>
                    </div>
                  </div>
                </div>

                {/* Testing Schedule */}
                <div>
                  <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                    Testing Schedule
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div>
                      <p className="text-xs text-gray-500">Last Tested</p>
                      <p className="text-sm">Jan 10, 2025</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Next Test Due</p>
                      <p className="text-sm font-medium flex items-center">
                        <Clock className="h-3 w-3 mr-1" />
                        Feb 10, 2025
                      </p>
                    </div>
                    <button className="w-full mt-3 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 text-sm font-medium">
                      Schedule Test
                    </button>
                  </div>
                </div>

                {/* Quick Actions */}
                <div>
                  <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                    Quick Actions
                  </h3>
                  <div className="space-y-2">
                    <button className="w-full flex items-center space-x-2 px-3 py-2 border rounded-lg hover:bg-gray-50 text-sm">
                      <Eye className="h-4 w-4" />
                      <span>View Evidence</span>
                    </button>
                    <button className="w-full flex items-center space-x-2 px-3 py-2 border rounded-lg hover:bg-gray-50 text-sm">
                      <Download className="h-4 w-4" />
                      <span>Upload Results</span>
                    </button>
                    <button className="w-full flex items-center space-x-2 px-3 py-2 border rounded-lg hover:bg-gray-50 text-sm">
                      <Edit3 className="h-4 w-4" />
                      <span>Edit Control</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProcessFlowchartVisualization;